import {
    Sprite,
    TilingSprite,
    NineSlicePlane,
    Container,
    Texture,
    Rectangle,
    Graphics
} from 'pixi.js';
import { GAMEPLAY, FONTS, LevelData, StaticLayoutItem } from '../../constants';
import { randInt, LoadContext, Disposable, Tween } from '../../utils';
import { Discoverable } from './Discoverable';
import { fonts, Focusable, FocusFrame, FocusManager, StyledBitmapText } from '../../ui';
import { Settings } from '../../settings';
import { GlowFilter } from '@pixi/filter-glow';

const GLOW = new GlowFilter({
    distance: 30,
    color: 0xffffff,
    outerStrength: 0,
    innerStrength: 4,
    quality: 0.02
});

const FRAME_WIDTH = 9;

export interface SimplePoint
{
    x: number;
    y: number;
}

export class SquareHighlight extends Container implements Focusable
{
    private sprite: Sprite;
    private focusBox: FocusFrame;
    private _highlight: boolean = false;
    private _origR: number;
    private _origG: number;
    private _origB: number;
    private _show: boolean = false;
    public focusable: boolean = false;

    constructor(size: number)
    {
        super();
        this.sprite = new Sprite(Texture.WHITE);
        this.sprite.anchor.set(0.5);
        this.sprite.tint = 0x9d9d9a;
        this._origR = this.r;
        this._origG = this.g;
        this._origB = this.b;

        this.sprite.visible = false;
        this.sprite.width = size;
        this.sprite.height = size;

        this.sprite.filters = [GLOW];
        this.addChild(this.sprite);

        this.focusBox = new FocusFrame(size, size);
        this.focusBox.position.set(-size/2, -size/2);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;

        this.interactiveChildren = false;
        this.hitArea = new Rectangle(-size/2, -size/2, size, size);
    }

    public get r() { return this.sprite.tint >> 16 & 0xff; }
    public set r(r)
    {
        this.sprite.tint = (this.sprite.tint & 0x00ffff) | r << 16;
    }

    public get g() { return this.sprite.tint >> 8 & 0xff; }
    public set g(g)
    {
        this.sprite.tint = (this.sprite.tint & 0xff00ff) | g << 8;
    }

    public get b() { return this.sprite.tint & 0xff; }
    public set b(b)
    {
        this.sprite.tint = (this.sprite.tint & 0xffff00) | b;
    }

    public get show() { return this._show; }
    public set show(show)
    {
        this._show = show;
        if (show)
        {
            this.sprite.visible = true;
        }
        else if (!this._highlight)
        {
            this.sprite.visible = false;
        }
    }

    public get highlight() { return this._highlight }
    public set highlight(highlight: boolean)
    {
        if (highlight && !this._highlight)
        {
            // Tween.get(this as Sprite, {loop: true}).to({alpha: 0.6}, 750, 'quadInOut').to({alpha: 1}, 750, 'quadInOut');
            Tween.get(this as SquareHighlight, {loop: true}).to({r: 0xff, g: 0xff, b: 0xff}, 750, 'quadInOut').to({r: this._origR, g: this._origG, b: this._origB}, 750, 'quadInOut');
            this._highlight = true;
            this.sprite.visible = true;
        }
        else if (!highlight)
        {
            Tween.removeTweens(this);
            // this.alpha = 1;
            this.r = this._origR;
            this.g = this._origG;
            this.b = this._origB;
            this._highlight = false;
            if (!this._show)
            {
                this.sprite.visible = false;
            }
        }
    }

    public get skip() { return !this.focusable; }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        if (this.interactive)
        {
            this.emit('pointertap');
        }
    }
}

export class Grid extends Container
{
    private frame: NineSlicePlane = null;
    private lines: TilingSprite[] = [];
    private bg: Sprite = null;
    private labels: StyledBitmapText[] = [];
    private labelBgs: Sprite[] = [];
    private fontSubscription: Disposable = null;
    public readonly gridWidth: number;
    public readonly gridHeight: number;
    public readonly squareSize: number;
    /** Non-player contents */
    public contents: Discoverable[][];
    public highlights: SquareHighlight[][];
    public highlightsReversed: SquareHighlight[][];
    private focusGrid: FocusFrame;
    public selectingRow: boolean = false;
    public selectingCol: boolean = false;

    /**
     * Static postions for player (p), parts (x), distractors (d), and battery (b).
     * Each position is a position pool, to be pulled from when a random coord is requested.
     */
    private staticLayout: {[id:string]: SimplePoint[]} = null;

    public static preload()
    {
        FocusFrame.preload();
        const loader = LoadContext.current;
        loader.add('GameAtlas', './assets/game/GameAtlas{size}.json');
    }

    constructor(gridData: LevelData, settings: Settings)
    {
        super();
        this.gridWidth = gridData.GRID_WIDTH;
        this.gridHeight = gridData.GRID_HEIGHT;
        this.squareSize = gridData.SQUARE_SIZE;

        if (gridData.STATIC_LAYOUT)
        {
            const layout = gridData.STATIC_LAYOUT;
            if (layout.length !== this.gridWidth * this.gridHeight)
            {
                throw new Error('Invalid array length for static layout of level!');
            }
            this.staticLayout = {};

            // index for accessing layout
            let i = 0;
            // cycle through the actual grid coordinates
            for (let y = this.gridHeight - 1; y >= 0; --y)
            {
                for (let x = 0; x < this.gridWidth; ++x)
                {
                    if (layout[i])
                    {
                        const type = layout[i];
                        if (this.staticLayout[type])
                        {
                            this.staticLayout[type].push({x, y});
                        }
                        else
                        {
                            this.staticLayout[type] = [{x, y}];
                        }
                    }
                    ++i;
                }
            }
        }


        const loader = LoadContext.current;
        this.bg = new Sprite(Texture.WHITE);
        this.bg.tint = 0x0C0904;
        this.bg.width = this.gridWidth * this.squareSize;
        this.bg.height = this.gridHeight * this.squareSize;
        this.addChild(this.bg);

        this.contents = [];
        this.highlights = [];
        for (let x = 0; x < this.gridWidth; ++x)
        {
            this.contents.push([]);
            this.highlights.push([]);
            for (let y = 0; y < this.gridHeight; ++y)
            {
                this.contents[x].push(null);
                const h = new SquareHighlight(this.squareSize);
                this.coordToPos({x, y}, h.position);
                this.addChild(h);
                this.highlights[x].push(h);
            }
        }

        let row1 = [];
        for (let i = 0; i < this.highlights.length; i++)
        {
            row1.push(this.highlights[i][0]);
        }

        this.highlightsReversed = [];
        this.highlightsReversed.push(row1);
        
        const labelStyle = fonts.GridLabel;
        const labelBG = FONTS.GridLabel[settings.textStyle.value].bg;

        const lineTex = loader.getSpritesheet('GameAtlas').textures['grid_line'];
        for (let i = 0; i < this.gridHeight; ++i)
        {
            if (i > 0)
            {
                // horizontal lines
                const line = new TilingSprite(lineTex, this.gridWidth * this.squareSize, lineTex.height);
                line.anchor.y = 0.5;
                line.y = this.squareSize * i;
                this.addChild(line);
                this.lines.push(line);
            }
            // y axis labels (numbers)
            const bg = new Sprite(Texture.WHITE);
            bg.width = bg.height = 66;
            bg.tint = labelBG;
            bg.anchor.set(1, 0.5);
            bg.x = -FRAME_WIDTH / 2;
            bg.y = this.bg.height - (this.squareSize * i) - this.squareSize / 2;
            this.addChild(bg);
            this.labelBgs.push(bg);
            const label = new StyledBitmapText(GAMEPLAY.Y_LABELS[i], labelStyle);
            label.anchor.set(0.5, 0.6);
            label.x = bg.x - bg.width / 2;
            label.y = bg.y;
            this.addChild(label);
            this.labels.push(label);
        }
        for (let i = 0; i < this.gridWidth; ++i)
        {
            // vertical lines
            if (i > 0)
            {
                const line = new TilingSprite(lineTex, this.gridHeight * this.squareSize, lineTex.height);
                line.anchor.y = 0.5;
                line.angle = 90;
                line.x = this.squareSize * i;
                this.addChild(line);
                this.lines.push(line);
            }
            // x axis labels (letters)
            const bg = new Sprite(Texture.WHITE);
            bg.width = bg.height = 66;
            bg.tint = labelBG;
            bg.anchor.set(0.5, 0);
            bg.x = this.squareSize * i + this.squareSize / 2;
            bg.y = this.bg.height + FRAME_WIDTH / 2;
            this.addChild(bg);
            this.labelBgs.push(bg);
            const label = new StyledBitmapText(GAMEPLAY.X_LABELS[i], labelStyle);
            label.anchor.set(0.5, 0.6);
            label.y = bg.y + bg.height / 2;
            label.x = bg.x;
            this.addChild(label);
            this.labels.push(label);
        }

        this.fontSubscription = Disposable.fromPropertySubscription(settings.textStyle, (value) => {
            const tint = FONTS.GridLabel[value].bg;
            for (const bg of this.labelBgs)
            {
                bg.tint = tint;
            }
        });

        this.frame = new NineSlicePlane(loader.getSpritesheet('GameAtlas').textures['grid_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        this.frame.width = this.bg.width + FRAME_WIDTH;
        this.frame.height = this.bg.height + FRAME_WIDTH;
        this.frame.x = -FRAME_WIDTH / 2;
        this.frame.y = -FRAME_WIDTH / 2;
        this.addChild(this.frame);

        setTimeout(this.createFocus.bind(this), 0);

    }

    public getContents(filter:(d:Discoverable)=>boolean): Discoverable[]
    {
        const out:Discoverable[] = [];
        for (let x = 0; x < this.gridWidth; ++x)
        {
            for (let y = 0; y < this.gridHeight; ++y)
            {
                const item = this.contents[x][y];
                if (!item) continue;
                if (filter && !filter(item)) continue;
                out.push(item);
            }
        }
        return out;
    }

    /**
     * Gets a grid coord from a global position. Returns null if outside the grid.
     */
    public gridCoordFromGlobal(global: SimplePoint): SimplePoint|null
    {
        const local = this.toLocal(global as any);
        if (local.x < 0 || local.y < 0 || local.x > this.bg.width || local.y > this.bg.height)
        {
            return null;
        }
        return {
            x: Math.floor(local.x / this.squareSize),
            y: this.gridHeight - Math.floor(local.y / this.squareSize) - 1
        };
    }

    /**
     * Returns an object with a position local to this Container, from a 0-based grid coordinate.
     * The position will be centered on the grid square.
     */
    public coordToPos(coord:SimplePoint, out?:SimplePoint)
    {
        if (!out)
        {
            out = {x:0, y:0};
        }
        out.x = (coord.x + 0.5) * this.squareSize;
        out.y = (this.gridHeight - coord.y - 0.5) * this.squareSize;
        return out;
    }

    public getRandomEmptyCoord(blocked:SimplePoint = {x:-1,y:-1}, type: StaticLayoutItem): SimplePoint
    {
        if (this.staticLayout)
        {
            const pool = this.staticLayout[type];
            if (!pool || !pool.length)
            {
                throw new Error(`Not enough placements in static layout for item ${type}`);
            }
            const i = randInt(0, pool.length - 1);
            const out = pool[i];
            pool.splice(i, 1);
            return out;
        }
        const out = {x: randInt(0, this.gridWidth - 1), y: randInt(0, this.gridHeight - 1)};
        while (this.contents[out.x][out.y] || (out.x === blocked.x && out.y === blocked.y))
        {
            out.x = randInt(0, this.gridWidth - 1);
            out.y = randInt(0, this.gridHeight - 1);
        }
        return out;
    }

    public ping(start: SimplePoint, direction: 'N'|'S'|'E'|'W', ignore:(d:Discoverable)=>boolean = ()=>false)
    {
        let dirX = 0;
        let dirY = 0;
        switch (direction)
        {
            case 'N':
                dirY = 1;
                break;
            case 'S':
                dirY = -1;
                break;
            case 'E':
                dirX = 1;
                break;
            case 'W':
                dirX = -1;
                break;
        }
        let {x, y} = start;
        // x += dirX;
        // y += dirY;
        const out:{found:Discoverable, coord:SimplePoint, coordDist: number} = {
            found: null,
            coord: {x: -1, y: -1},
            coordDist: 0
        };
        while (x >= 0 && x < this.gridWidth && y >= 0 && y < this.gridHeight)
        {
            const found = this.contents[x][y];
            if (found && found.state !== Discoverable.State.Found && !ignore(found))
            {
                out.found = found;
                out.coord.x = x;
                out.coord.y = y;
                out.coordDist = Math.abs(x - start.x) + Math.abs(y - start.y);
                break;
            }

            x += dirX;
            y += dirY;
        }
        if (!out.found)
        {
            // return distance to wall
            out.coordDist = (Math.abs(x - start.x - dirX) + Math.abs(y - start.y - dirY));
        }
        return out;
    }

    protected createFocus()
    {
        const width = (this.gridWidth * this.squareSize) + 15;
        const height = (this.gridHeight * this.squareSize) + 15;
        this.focusGrid = new FocusFrame(width, height);
        
        this.focusGrid.position.set(-8, -8);
        this.addChild(this.focusGrid);
        this.focusGrid.visible = false;
        this.hitArea = new Rectangle(0, 0, width, height);
    }

    public get focus() { return this.focusGrid.visible; }
    public set focus(focused) {
        this.focusGrid.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusGrid.visible; }
    public set focusVisible(visible) {
        this.focusGrid.visible = visible;
    }

    public activate()
    {
        this.selectingRow = true;
        FocusManager.instance.activateContext(this.highlightsReversed[0]);
        if (this.interactive)
        {
            this.emit('pointertap');
        }
    }

    public destroy()
    {
        super.destroy({children: true});
        this.fontSubscription.destroy();
    }
}